import { useEffect, useState } from "react";

import { swSettings } from "app/common/services/swSettings";
import { ChangeSolutionModal } from "app/components/ChangeSolutionModal/ChangeSolutionModal";
import { SWITCH_SOLUTION_KEY } from "app/components/ChangeSolutionModal/consts";
import { usePageTopBannerStripReveal } from "app/components/PageTopBanner/bannerRevealContext";
import AfterTrialModal from "app/components/React/TrialBanner/AfterTrialModal/AfterTrialModal";
import BannerCTA from "app/components/React/TrialBanner/BannerCTA/BannerCTA";
import DiscountBanner from "app/components/React/TrialBanner/DiscountBanner/DiscountBanner";
import { TrialBannerContent } from "app/components/React/TrialBanner/StyledTrialBanner";
import { TrialBannerDict, useTrialBanner } from "app/components/React/TrialBanner/useTrialBanner";
import { TRIAL_BANNER_ID } from "app/common/constants/trialBanner";
import { useFitScoreContext } from "app/contexts/FitScoreContext/FitScoreContext";
import { i18nFilter } from "app/filters/i18nFilter";
import { isNewHomepage } from "app/pages/digital-suite/home-page/helpers";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { allTrackers } from "app/services/track/track";
import { getQueryParam, removeQueryFromUrl } from "app/UtilitiesAndConstants/UtilityFunctions/queryUtils";
import { isAfterTrial, isAllowSwitchSolution, isSI } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import { LeftFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

export const i18n = i18nFilter();

const CHANGE_SOLUTION_QUERY_PARAM = "allowSwitchSolution";

const TrialBanner = () => {
    const { trialBannerType, isSticky, isInDiscount, shouldGoToAfterTrial, isAccountCreatedToday } = useTrialBanner();

    const [isAfterTrialModalOpen, setIsAfterTrialModalOpen] = useState<boolean>(false);
    const [allowSwitchSolutionOpen, setAllowSwitchSolution] = useState<boolean>(false);

    const stripProps = usePageTopBannerStripReveal();

    const isSalesApp = proNavigator.isSalesSolution();

    const { isEvaluated: isFitScoreEvaluated } = useFitScoreContext();

    const shouldOpenSwitchSolutionModal =
        !PreferencesService.get(SWITCH_SOLUTION_KEY) &&
        getQueryParam(CHANGE_SOLUTION_QUERY_PARAM) &&
        isAllowSwitchSolution(swSettings);

    useEffect(() => {
        // Only run after fit score has been evaluated
        if (!isFitScoreEvaluated) {
            return;
        }

        if (window.SwHook) {
            if (isAfterTrial(swSettings) && !isSI(swSettings) && !isInDiscount) {
                const isPopupAppeared = PreferencesService.get(`popupAfterTrialAppeared`);
                if (!isPopupAppeared) {
                    setIsAfterTrialModalOpen(true);
                    PreferencesService.add({ [`popupAfterTrialAppeared`]: true });
                }
            }
        }
    }, [isInDiscount, isFitScoreEvaluated]);

    useEffect(() => {
        if (shouldOpenSwitchSolutionModal) {
            setAllowSwitchSolution(true);
            removeQueryFromUrl(CHANGE_SOLUTION_QUERY_PARAM);
        }
    }, [shouldOpenSwitchSolutionModal]);

    const handleCloseAfterTrialModal = () => {
        allTrackers.dispatchTrackEvent(
            "Pro Hooks 2.0",
            "close",
            `package/${isAccountCreatedToday ? "trialEndedCCflow" : "trialEnded"}/${LocationService.getCurrentLocationTracking()}`,
        );
        setIsAfterTrialModalOpen(false);
    };

    return (
        <>
            {trialBannerType === TrialBannerDict.DISCOUNT_BANNER ? (
                <DiscountBanner isSticky={isSticky} />
            ) : (
                <TrialBannerContent
                    {...stripProps}
                    isSticky={isSticky}
                    id={TRIAL_BANNER_ID}
                    justify={isNewHomepage() ? "end" : "center"}
                    data-automation="trial-banner"
                >
                    <LeftFlexRow>
                        <BannerCTA
                            isSalesApp={isSalesApp}
                            shouldGoToAfterTrial={shouldGoToAfterTrial}
                        />
                    </LeftFlexRow>
                </TrialBannerContent>
            )}
            <AfterTrialModal
                isAfterTrialModalOpen={isAfterTrialModalOpen}
                onCloseHookModal={handleCloseAfterTrialModal}
            />
            <ChangeSolutionModal
                isOpen={allowSwitchSolutionOpen}
                onClose={() => setAllowSwitchSolution(false)}
            />
        </>
    );
};

export default TrialBanner;
